"use client";

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useClickOutsideHandler } from '@/hooks/useClickOutsideHandler';
import { X, Tag, Truck, MapPin, Loader2 } from '@/components/icons';
import { ShoppingBasket as ShoppingCart, Plus, Heart, ChevronLeft, ChevronRight, Pause, Play, ChevronDown, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn, offcanvasSidebarPanelClass } from '@/lib/utils';
import { Link } from 'react-router-dom';
import { format, isPast } from 'date-fns';
import useHeaderOffset from '@/hooks/useHeaderOffset';
import { es } from 'date-fns/locale';
import { useToast } from '@/components/ui/use-toast';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { Session } from '@supabase/supabase-js';
import { translateCondition, formatCurrency, translateProductType, translateVehicleDetail, toSentenceCase, translateProductCategoryLabel, translateGroceriesSubcategoryLabel } from '@/lib/utils';
import { addCartItem, updateCartItem, getCartForUser } from '@/lib/supabase/carts';
import { addToTempCart, updateTempCartItemQuantity, getTempCart } from '@/lib/tempCart';
import { supabase, getProductById } from '@/lib/supabase';
import { getStoreById, isUserOwnerOrAdmin } from '@/lib/supabase/stores';
import { ChatButton } from '@/components/chat/ChatButton';
import FlipCounter from '@/components/FlipCounter';
import { Gavel, DollarSign, Tree, Info, Star, Clock, Car, HardDrive, Beaker, Repeat, Paintbrush } from '@/components/icons';
import ProductActivityFeed from '@/components/ProductActivityFeed';
import ImageViewer from '@/components/ImageViewer';
import ProductCatalogComments from '@/components/ProductCatalogComments';
import ProductChatForm from '@/components/ProductChatForm';
import { getProductChatsByProductId, getAverageRatingByProductId } from '@/lib/supabase/productChats';
import ProductChatItem from '@/components/ProductChatItem';
import MiniBidForm from '@/components/MiniBidForm';
import CountdownTimer from '@/components/CountdownTimer';
import { usePullToClose } from '@/hooks/usePullToClose';
import { useSidebarWidth } from '@/hooks/useSidebarWidth';
import { RealtimeChannel } from '@supabase/supabase-js';
import { useContentSidebar } from '@/context/ContentSidebarContext';
import { QuantitySelector } from '@/components/QuantitySelector';
import { usePlayBeep } from '@/hooks/usePlayBeep';
import { usePlayFavoriteSound } from '@/hooks/usePlayFavoriteSound';
import { useVibrate } from '@/hooks/useVibrate';
import { useFavorites } from '@/hooks/useFavorites';
import { useCheckout } from '@/context/CheckoutContext';
import { useScrollLock } from '@/hooks/useScrollLock';
import EditProductSidebar from '@/components/store/EditProductSidebar';

interface ProductSidebarProps {
  product: any;
  isOpen: boolean;
  onClose: () => void;
  session: Session | null;
  store?: any;
}

export const ProductSidebar: React.FC<ProductSidebarProps> = ({ product: initialProduct, isOpen, onClose, session, store }) => {
  const [product, setProduct] = useState(initialProduct);
  const [isClosing, setIsClosing] = useState(false);
  const [isMinimizing, setIsMinimizing] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const imageViewerFirstRenderRef = useRef(true);
  const [cartItemId, setCartItemId] = useState<string | null>(null);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [currentQuantity, setCurrentQuantity] = useState(0);
  const [fayeEntryMode, setFayeEntryMode] = useState(false);
  const [panningProgressNow, setPanningProgressNow] = useState(() => Date.now());
  const panningProgressRafRef = useRef<number | null>(null);
  const scrollResumeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollPausedRef = useRef(false);
  const lastScrollTopRef = useRef(0);
  const [canEditProduct, setCanEditProduct] = useState(false);
  const [editProductSidebarOpen, setEditProductSidebarOpen] = useState(false);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  const [updatingProductIds, setUpdatingProductIds] = useState<Set<string>>(new Set());
  const {
    minimizedProducts,
    toggleProductMinimized,
    clearMinimizedProduct,
    minimizedNotes,
    setShowProductClickOverlay,
    productSidebarFullscreen,
    setProductSidebarFullscreen,
    productPanningActive,
    productPanningIntervalMs,
    productPanningStepStartedAt,
    productPanningElapsedMs,
    productPanningIndex,
    productPanningQueueLength,
    pauseProductPanning,
    resumeProductPanning,
    stopProductPanning,
    toggleProductPanningPlayback,
    goToNextPanningProduct,
    goToPreviousPanningProduct,
  } = useContentSidebar();
  const isMinimized = product?.id ? minimizedProducts[product.id] : false;
  const showPanningControls = productPanningQueueLength > 0;
  const productBackdropImage = product?.main_image_url || product?.cover_url || product?.image_urls?.[0] || null;
  const panningRingRadius = 19;
  const panningRingCircumference = 2 * Math.PI * panningRingRadius;
  const effectivePanningElapsedMs = productPanningActive
    ? Math.min(productPanningIntervalMs, Math.max(0, panningProgressNow - productPanningStepStartedAt))
    : productPanningElapsedMs;
  const panningProgressRatio = productPanningIntervalMs > 0
    ? Math.max(0, Math.min(1, effectivePanningElapsedMs / productPanningIntervalMs))
    : 0;
  
  // Hooks for sounds and vibrations
  const playBeep = usePlayBeep();
  const playFavoriteSound = usePlayFavoriteSound();
  const vibrate = useVibrate();
  const { toggleFavorite, isFavorited } = useFavorites();
  const { openSidebar: openCheckoutSidebar } = useCheckout();

  // Use useCallback for handleClose and define it at the top
  const handleClose = React.useCallback(() => {
    setShowProductClickOverlay(false);
    setIsClosing(true);
    stopProductPanning();
    setTimeout(() => {
      if (product?.id) {
        clearMinimizedProduct(product.id);
      }
      setProductSidebarFullscreen(false);
      onClose();
      setIsClosing(false);
    }, 300); // Match animation duration
  }, [onClose, product?.id, clearMinimizedProduct, setShowProductClickOverlay, setProductSidebarFullscreen, stopProductPanning]);

  // Callbacks for EditProductSidebar
  const handleEditProductClose = useCallback(() => {
    setEditProductSidebarOpen(false);
  }, []);

  const handleEditProductUpdated = useCallback(() => {
    // Refresh product data after edit - don't reset cart state during this update
    if (initialProduct?.id) {
      getProductById(initialProduct.id)
        .then(updatedProduct => {
          // Only update product data, preserve cart state
          // Make sure we have valid product data before updating
          if (updatedProduct && updatedProduct.id) {
            setProduct(updatedProduct);
          } else {
            console.warn('Invalid product data received:', updatedProduct);
            // Keep the current product state if fetch fails
          }
        })
        .catch(e => {
          console.warn('Error refreshing product:', e);
          // Keep the current product state if fetch fails
        });
    }
  }, [initialProduct?.id]);

  useEffect(() => {
    return () => {
      if (panningProgressRafRef.current !== null) {
        cancelAnimationFrame(panningProgressRafRef.current);
      }
      if (scrollResumeTimeoutRef.current) {
        clearTimeout(scrollResumeTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!showPanningControls) return;
    if (!productPanningActive) {
      setPanningProgressNow(Date.now());
      return;
    }

    const tick = () => {
      setPanningProgressNow(Date.now());
      panningProgressRafRef.current = requestAnimationFrame(tick);
    };

    panningProgressRafRef.current = requestAnimationFrame(tick);

    return () => {
      if (panningProgressRafRef.current !== null) {
        cancelAnimationFrame(panningProgressRafRef.current);
        panningProgressRafRef.current = null;
      }
    };
  }, [productPanningActive, productPanningStepStartedAt, showPanningControls]);

  const pausePanningForCart = React.useCallback(() => {
    if (!productPanningActive) return;
    scrollPausedRef.current = false;
    stopProductPanning();
  }, [productPanningActive, stopProductPanning]);

  useEffect(() => {
    const viewport = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]') as HTMLDivElement | null;
    if (!viewport || showPanningControls === false) return;

    const clearScheduledResume = () => {
      if (scrollResumeTimeoutRef.current) {
        clearTimeout(scrollResumeTimeoutRef.current);
        scrollResumeTimeoutRef.current = null;
      }
    };

    const handleScroll = () => {
      const nextTop = viewport.scrollTop;
      const movedDown = nextTop > lastScrollTopRef.current;
      lastScrollTopRef.current = nextTop;

      if (nextTop > 0) {
        clearScheduledResume();
        if (productPanningActive) {
          scrollPausedRef.current = true;
          pauseProductPanning();
        }
        return;
      }

      if (nextTop <= 0 && scrollPausedRef.current) {
        clearScheduledResume();
        scrollResumeTimeoutRef.current = setTimeout(() => {
          if (viewport.scrollTop <= 0 && scrollPausedRef.current && !productPanningActive) {
            scrollPausedRef.current = false;
            resumeProductPanning({ advanceToNext: false });
          }
        }, 500);
        return;
      }

      if (!movedDown && nextTop <= 0) {
        clearScheduledResume();
      }
    };

    viewport.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      clearScheduledResume();
      viewport.removeEventListener('scroll', handleScroll);
    };
  }, [pauseProductPanning, productPanningActive, resumeProductPanning, showPanningControls]);

  const handleMinimize = React.useCallback(() => {
    if (isMinimizing) return; // Prevent double minimize
    stopProductPanning();
    setIsMinimizing(true);
    setTimeout(() => {
      if (product?.id) {
        toggleProductMinimized(product.id);
      }
      setIsMinimizing(false);
    }, 300);
  }, [product?.id, stopProductPanning, toggleProductMinimized, isMinimizing]);

  const handleMaximize = React.useCallback(() => {
    if (product?.id && minimizedProducts[product.id]) {
      toggleProductMinimized(product.id);
      return;
    }
    setProductSidebarFullscreen(true);
  }, [product?.id, minimizedProducts, toggleProductMinimized, setProductSidebarFullscreen]);

  const handleHeaderMinimize = React.useCallback(() => {
    if (productSidebarFullscreen) {
      setProductSidebarFullscreen(false);
      return;
    }
    handleMinimize();
  }, [productSidebarFullscreen, setProductSidebarFullscreen, handleMinimize]);

  const [viewerStartIndex, setViewerStartIndex] = useState(0);
  const [isReady, setIsReady] = useState(false);

  // Pull-to-close: ref al ScrollArea
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  useScrollLock((isOpen && !isMinimized) || isClosing || isMinimizing, false, scrollAreaRef);

  const [lastPurchaseInfo, setLastPurchaseInfo] = useState<{ storeName?: string; purchaseDate: string } | null>(null);
  const [averageRating, setAverageRating] = useState<number | null>(null);
  const [productChats, setProductChats] = useState<any[]>([]);
  const [auctionEndedByTimer, setAuctionEndedByTimer] = useState(false);
  const isAuctionActive = product.is_auction && product.auction_end_time && !isPast(new Date(product.auction_end_time)) && !auctionEndedByTimer;
  const sidebarRef = useRef<HTMLDivElement>(null);
  const { headerHeight, isDesktop, bottomNavHeight, recalculate } = useHeaderOffset();
  const isFayeModeActive = true;
  const isFayeDesktopMode = true;
  const { maxWidth } = useSidebarWidth();
  const { toast } = useToast();

  useEffect(() => {
    const handleFayeStart = () => {
      setFayeEntryMode(true);
    };

    window.addEventListener('faye-mode-start', handleFayeStart);
    return () => {
      window.removeEventListener('faye-mode-start', handleFayeStart);
    };
  }, []);

  useEffect(() => {
    if (showPanningControls) {
      setFayeEntryMode(true);
      return;
    }

    if (!isOpen && !isClosing) {
      setFayeEntryMode(false);
    }
  }, [isOpen, isClosing, showPanningControls]);

  // Wait for dimensions to be calculated before showing sidebar
  // On desktop, headerHeight can be 0 when the header is hidden (scrolled away) — still ready
  useEffect(() => {
    if (!isReady && (headerHeight > 0 || isDesktop)) {
      // Small delay to ensure all layout calculations are complete
      const timer = setTimeout(() => setIsReady(true), 50);
      return () => clearTimeout(timer);
    }
  }, [headerHeight, isDesktop, isReady]);

  // Activar pull-to-close (solo en móvil)
  usePullToClose(scrollAreaRef, {
    onClose: handleClose,
    threshold: 80,
    enabled: isOpen && !isDesktop && !isImageViewerOpen
  });

  // Sincronizar producto inicial y resetear isReady cuando cambia
  useEffect(() => {
    setProduct(initialProduct);
    // Keep sidebar mounted during Faye transitions to avoid visible flicker.
    if (!isOpen) {
      setIsReady(false);
    }
    // Reset cart state so it gets re-fetched for new product
    setCartItemId(null);
    setCurrentQuantity(0);
    // Reset image viewer ref when sidebar opens/closes
    imageViewerFirstRenderRef.current = true;
  }, [initialProduct?.id, isOpen]); // Only reset when product ID changes

  // Escuchar cambios en tiempo real del producto (para actualizar pujas, etc.)
  useEffect(() => {
    if (!isOpen || !initialProduct?.id) return;
    // Load current cart quantity for this product when sidebar opens
    // Only update cart state when sidebar first opens with this product
    const loadCartQuantity = async () => {
      if (session?.user?.id && initialProduct.store_id) {
        try {
          const cart = await getCartForUser(session.user.id, initialProduct.store_id);
          if (cart?.cart_items) {
            const item = (cart.cart_items as any[]).find((i: any) => i.product_id === initialProduct.id);
            if (item) {
              setCartItemId(item.id);
              setCurrentQuantity(item.quantity);
            } else {
              // Product not in cart, ensure clean state
              setCartItemId(null);
              setCurrentQuantity(0);
            }
          }
        } catch (e) {
          // silently ignore
        }
      } else if (!session?.user?.id) {
        const tempCart = getTempCart(initialProduct.store_id);
        if (tempCart?.items) {
          const item = tempCart.items.find((i: any) => i.product_id === initialProduct.id || i.id === initialProduct.id);
          if (item) {
            setCartItemId(initialProduct.id);
            setCurrentQuantity(item.quantity);
          } else {
            // Product not in temp cart, ensure clean state
            setCartItemId(null);
            setCurrentQuantity(0);
          }
        } else {
          // No temp cart, ensure clean state
          setCartItemId(null);
          setCurrentQuantity(0);
        }
      }
    };
    loadCartQuantity();
  }, [isOpen, initialProduct?.id, session?.user?.id]);

  // Escuchar cambios en tiempo real del producto (para actualizar pujas, etc.)
  useEffect(() => {
    if (!initialProduct?.id) return;

    const channel: RealtimeChannel = supabase
      .channel(`product_sidebar_updates_${initialProduct.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'products',
          filter: `id=eq.${initialProduct.id}`
        },
        async (_payload) => {
          // Re-fetch producto para actualizar current_bid y otros campos
          try {
            const updatedProduct = await getProductById(initialProduct.id);
            if (updatedProduct) {
              setProduct(updatedProduct);
            }
          } catch (e) {
            console.warn('ProductSidebar: realtime update refetch failed', e);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [initialProduct?.id]);


  // Recalcular medidas cuando el sidebar se abre
  useEffect(() => {
    if (isOpen) {
      recalculate();
      // Recalcular también después de un pequeño delay para asegurar que el DOM esté actualizado
      const timeoutId = setTimeout(recalculate, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, recalculate]);

  const handleImageClick = (index: number) => {
    setViewerStartIndex(index);
    setIsImageViewerOpen(true);
  };

  const handleCloseImageViewer = () => {
    setIsImageViewerOpen(false);
  };

  // Pause/resume product panning when image viewer opens/closes
  useEffect(() => {
    if (imageViewerFirstRenderRef.current) {
      imageViewerFirstRenderRef.current = false;
      return;
    }
    if (isImageViewerOpen) {
      stopProductPanning();
    } else {
      resumeProductPanning();
    }
  }, [isImageViewerOpen]); // eslint-disable-next-line react-hooks/exhaustive-deps


  // Click fuera para cerrar, pero NO si el visor de imágenes está abierto
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && !isImageViewerOpen && sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        const el = event.target as HTMLElement | null;
        if (!el || !el.isConnected) return; // Ignore if element is no longer in the DOM

        // Check if we are clicking on a portal (like Select, Popovers or Dialogs) or Leaflet map
        const isPortal = el.closest('[data-radix-portal]') || 
                        el.closest('[data-radix-popper-content-wrapper]') ||
                        el.closest('[role="dialog"]') ||
                        el.closest('[role="listbox"]') ||
                        el.closest('.leaflet-container') ||
                        el.closest('[toast-close]') ||
                        el.closest('[data-radix-toast-announcer]');
        if (isPortal) return;

        // Ignore clicks originating from chat windows so closing/minimizing chat doesn't close this sidebar
        if (el.closest && el.closest('[data-chat-window]')) return;
        // Ignore clicks from toast close buttons
        if (el.closest && el.closest('[toast-close]')) return;
        // Ignore clicks from edit product sidebar
        if (el.closest && el.closest('[data-edit-product-sidebar]')) return;
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, isImageViewerOpen, handleClose]);

  // Cerrar con tecla ESC
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        event.preventDefault();
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleClose]);

  const publishedString = product?.created_at && !isNaN(new Date(product.created_at).getTime()) 
    ? format(new Date(product.created_at), "dd/MM/yyyy 'a las' HH:mm", { locale: es }) 
    : '';

  const lastPurchasePurchaseDate = lastPurchaseInfo?.purchaseDate && !isNaN(new Date(lastPurchaseInfo.purchaseDate).getTime())
    ? lastPurchaseInfo.purchaseDate
    : null;

  // Get image URLs
  let displayImageUrls: string[] = [];
  if (product) {
    if (product.main_image_url) {
      displayImageUrls.push(product.main_image_url);
      if (product.image_urls && Array.isArray(product.image_urls)) {
        product.image_urls.forEach((url: string) => {
          if (url !== product.main_image_url) {
            displayImageUrls.push(url);
          }
        });
      }
    } else if (product.image_urls && Array.isArray(product.image_urls)) {
      displayImageUrls = [...product.image_urls];
    } else if (product.cover_url) {
      displayImageUrls = [product.cover_url];
    }
  }

  const translatedCondition = product ? translateCondition(product.condition) : '';
  const hasDescription = Boolean(String(product?.description || '').trim());
  const hasAuthor = Boolean(String(product?.author || '').trim());

  // Compute minimum rental option (if any) to display rental info
  let minRentalOption: any = null;
  if (product && product.rental_options && Array.isArray(product.rental_options) && product.rental_options.length > 0) {
    const validOpts = product.rental_options.filter((o: any) => o && typeof o.price === 'number' && !isNaN(o.price));
    if (validOpts.length > 0) {
      minRentalOption = validOpts.reduce((min: any, cur: any) => (cur.price < min.price ? cur : min), validOpts[0]);
    }
  }

  const locationString = product?.location_name
    ? product.location_name.split(',')[0]
    : null;
  const hasRealCondition = translatedCondition && translatedCondition !== 'No especificada';
  const detailTagsCount = (hasRealCondition ? 1 : 0) + (product?.free_shipping ? 1 : 0) + (locationString ? 1 : 0);
  const hasVehicleDetails = Boolean(
    product?.product_type === 'vehicle' &&
    product?.details?.vehicle &&
    (
      product.details.vehicle.year ||
      product.details.vehicle.kilometers ||
      product.details.vehicle.fuel_type ||
      product.details.vehicle.transmission ||
      product.details.vehicle.color ||
      product.details.vehicle.gnc
    )
  );
  const hasElectronicsDetails = Boolean(
    product?.product_type === 'electronics' &&
    product?.details?.electronics &&
    (
      product.details.electronics.brand ||
      product.details.electronics.model ||
      product.details.electronics.storage ||
      product.details.electronics.ram
    )
  );
  const hasTechnicalDetails = hasVehicleDetails || hasElectronicsDetails;

  const categoryString = product?.product_type ? translateProductCategoryLabel(product.product_type) : null;
  const [canMessageOwner, setCanMessageOwner] = useState<boolean>(false);
  const [checkingCanMessage, setCheckingCanMessage] = useState<boolean>(false);
  const [fetchedStore, setFetchedStore] = useState<any | null>(null);
  const [fetchedProfile, setFetchedProfile] = useState<any | null>(null);
  const [isLoadingSellerData, setIsLoadingSellerData] = useState<boolean>(false);

  const effectiveStore = store || product?.stores || fetchedStore;
  const sellerUsername = product?.profiles?.username || fetchedProfile?.username || null;
  const sellerAvatarUrl = product?.profiles?.avatar_url || fetchedProfile?.avatar_url;
  const storeDisplayName = effectiveStore?.name || effectiveStore?.slug || null;
  const hasStorePublisher = Boolean(product?.store_id && effectiveStore && storeDisplayName);
  const hasProfilePublisher = Boolean(!product?.store_id && sellerUsername);
  const shouldShowPublisherBlock = Boolean(isLoadingSellerData || hasStorePublisher || hasProfilePublisher);

  useEffect(() => {
    const fetchExtraData = async () => {
      if (!product) return;
      
      // Check if we need to load seller data
      const needsStore = !effectiveStore && product.store_id;
      const needsProfile = !(product?.profiles?.username) && product.seller_id;
      
      if (needsStore || needsProfile) {
        setIsLoadingSellerData(true);
      }
      
      // Fetch store if missing
      if (needsStore) {
        const { data } = await supabase.from('stores').select('*').eq('id', product.store_id).maybeSingle();
        if (data) setFetchedStore(data);
      }
      
      // Fetch profile if missing
      if (needsProfile) {
        const { data } = await supabase.from('profiles').select('*').eq('id', product.seller_id).maybeSingle();
        if (data) setFetchedProfile(data);
      }
      
      setIsLoadingSellerData(false);
    };
    
    fetchExtraData();
  }, [product?.id, product?.store_id, product?.seller_id, !!effectiveStore]);


  const [ownerId, setOwnerId] = useState<string | null>(effectiveStore?.owner_id || product?.seller_id || null);

  useEffect(() => {
    if (effectiveStore?.owner_id) {
      setOwnerId(effectiveStore.owner_id);
    } else if (product?.seller_id) {
      setOwnerId(product.seller_id);
    }
  }, [effectiveStore, product?.seller_id]);

  // Validate edit permissions for product
  useEffect(() => {
    const checkEditPermissions = async () => {
      if (!session?.user?.id || !product?.id) {
        setCanEditProduct(false);
        return;
      }

      try {
        // If product belongs to a store, check if user is owner or admin
        if (product.store_id) {
          const canEdit = await isUserOwnerOrAdmin(product.store_id);
          setCanEditProduct(canEdit);
        } else {
          // If product is directly owned by user (no store), check if seller_id matches
          setCanEditProduct(product.seller_id === session.user.id);
        }
      } catch (e) {
        console.warn('Error checking edit permissions:', e);
        setCanEditProduct(false);
      }
    };

    checkEditPermissions();
  }, [session?.user?.id, product?.id, product?.store_id, product?.seller_id]);

  // Pause panning when edit sidebar is opened
  useEffect(() => {
    if (editProductSidebarOpen && productPanningActive) {
      pauseProductPanning();
    }
  }, [editProductSidebarOpen, productPanningActive, pauseProductPanning]);

  useEffect(() => {
    let mounted = true;
    const check = async () => {
      // Ensure we have the owner id from the store if not provided
      if (!ownerId && product?.store_id) {
        try {
          const s = await getStoreById(product.store_id);
          if (mounted && s?.owner_id) setOwnerId(s.owner_id);
        } catch (e) {
          console.warn('Error fetching store for ownerId:', e);
        }
      }
      
      if (!session?.user?.id || !product) {
        if (mounted) setCanMessageOwner(false);
        return;
      }

      // If product is catalog-linked, enforce verified-purchase check
      if (product.product_catalog_id) {
        try {
          if (mounted) setCheckingCanMessage(true);
          const { data, error } = await supabase.rpc('can_comment_on_catalog_product', {
            p_user_id: session.user.id,
            p_product_catalog_id: product.product_catalog_id
          });
          if (!mounted) return;
          if (error) {
            console.warn('can_comment_on_catalog_product check failed', error);
            setCanMessageOwner(false);
          } else {
            const valid = data && Array.isArray(data) && data.length > 0 && data[0] && data[0].can_comment === true;
            setCanMessageOwner(!!valid);
          }
        } catch (e) {
          console.error('Error checking permission to message owner:', e);
          if (mounted) setCanMessageOwner(false);
        } finally {
          if (mounted) setCheckingCanMessage(false);
        }
      } else {
        // Non-catalog products: allow any logged-in user to message owner
        if (mounted) setCanMessageOwner(!!session?.user?.id);
      }
    };
    check();
    return () => { mounted = false; };
  }, [product.product_catalog_id, session?.user?.id]);

  // Fetch purchase history
  useEffect(() => {
    const fetchPurchaseHistory = async () => {
      if (!session?.user?.id || !product.store_id) return;

      try {
        const { data, error } = await supabase
          .from('orders')
          .select('id, created_at, store_id')
          .eq('user_id', session.user.id)
          .eq('store_id', product.store_id)
          .in('status', ['completed', 'fulfilled'])
          .order('created_at', { ascending: false })
          .limit(1);

        if (!error && data && data.length > 0) {
          const order = data[0];
          const purchaseDate = format(new Date(order.created_at), "dd 'de' MMMM 'de' yyyy", { locale: es });
          setLastPurchaseInfo({ storeName: effectiveStore?.name, purchaseDate });
        }
      } catch (e) {
        console.warn('Error fetching purchase history:', e);
      }
    };

    fetchPurchaseHistory();
  }, [session?.user?.id, product.store_id, effectiveStore?.name]);

  // Fetch average rating
  useEffect(() => {
    const fetchAverageRating = async () => {
      if (!product.id) return;

      try {
        const rating = await getAverageRatingByProductId(product.id);
        setAverageRating(rating);
      } catch (e) {
        console.warn('Error fetching average rating:', e);
      }
    };

    fetchAverageRating();
  }, [product.id]);

  // Fetch product chats
  useEffect(() => {
    const fetchProductChats = async () => {
      if (!product.id) return;

      try {
        const chats = await getProductChatsByProductId(product.id);
        setProductChats(chats || []);
      } catch (e) {
        console.warn('Error fetching product chats:', e);
      }
    };

    fetchProductChats();
  }, [product.id]);


  // Compute if we should actually show the animation
  const shouldShow = isOpen && !isClosing && isReady;

  // Minimized view
  if (isMinimized) {
    // Stack product sidebar on top of note sidebars. Count how many notes are minimized
    const minimizedNoteCount = Object.values(minimizedNotes).filter(Boolean).length;
    const minimizedProductBottomOffset = !isDesktop ? bottomNavHeight + (minimizedNoteCount * 64) : 0;
    return (
      <div
        className={cn(
          'fixed bottom-0 w-80 bg-background border rounded-t-lg shadow-lg z-80 max-md:fixed max-md:w-full max-md:left-0 max-md:right-0 max-md:rounded-none',
          'transition-all duration-300',
          isClosing && 'animate-out slide-out-to-bottom duration-300'
        )}
        style={{ 
          right: isDesktop ? 'auto' : undefined, 
          bottom: !isDesktop ? `${minimizedProductBottomOffset}px` : undefined,
        }}
      >
        <div
          role="button"
          onClick={handleMaximize}
          className="w-full p-3 flex items-center justify-between transition-colors rounded-t-lg max-md:rounded-none hover:bg-muted/50 cursor-pointer"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-muted/50 flex-shrink-0" />
            <span className="font-medium truncate">{product.title}</span>
          </div>
          <div
            role="button"
            className="h-6 w-6 hover:bg-primary/20 rounded flex items-center justify-center transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
          >
            <X className="w-4 h-4" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Overlay */}
      {(isOpen || isClosing) && (
        <div
          className={cn(
            'fixed inset-0 z-[9997] transition-opacity duration-300',
            isFayeDesktopMode ? 'bg-black/35' : 'bg-transparent',
            (!shouldShow || isClosing) && 'opacity-0 pointer-events-none'
          )}
          onClick={handleClose}
        >
          {isFayeDesktopMode && productBackdropImage ? (
            <>
              <div
                className="absolute inset-0 bg-center bg-cover scale-110 blur-3xl opacity-65"
                style={{ backgroundImage: `url(${productBackdropImage})` }}
              />
              <div className="absolute inset-0 bg-background/70 backdrop-blur-2xl" />
            </>
          ) : null}
          {isFayeDesktopMode && !productBackdropImage ? (
            <div className="absolute inset-0 bg-background/45 backdrop-blur-2xl" />
          ) : null}
        </div>
      )}

      <div
      ref={sidebarRef}
      className={cn(
        'fixed w-full lg:w-[400px] bg-background z-[9999] flex flex-col overflow-hidden',
        isFayeDesktopMode ? 'left-1/2 right-auto' : 'right-0',
        isFayeDesktopMode
          ? (shouldShow ? 'opacity-100 shadow-2xl' : 'opacity-0 shadow-none')
          : offcanvasSidebarPanelClass(shouldShow, isClosing)
      )}
      style={{
        top: (productSidebarFullscreen || isFayeModeActive) ? 0 : (isDesktop ? `${headerHeight}px` : '0'),
        bottom: productSidebarFullscreen ? 0 : (!isDesktop ? 0 : undefined),
        height: (productSidebarFullscreen || isFayeModeActive)
          ? '100dvh'
          : (isDesktop ? `calc(100vh - ${headerHeight}px)` : '100dvh'),
        transform: isFayeDesktopMode && !productSidebarFullscreen
          ? 'translateX(-50%)'
          : undefined,
        pointerEvents: shouldShow ? 'auto' : 'none',
        transition: isFayeDesktopMode ? 'opacity 220ms ease-out' : 'transform 300ms ease-out',
        willChange: 'transform, opacity',
      }}
    >
      {!product ? (
        <div className="flex-1 flex items-center justify-center p-8 bg-background">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : (
        <>
      {/* Safe Area Spacer for Mobile */}
      {!isDesktop && <div className="h-[env(safe-area-inset-top,0px)] bg-primary w-full shrink-0" />}
      {/* Header - Compacto y funcional */}
      <div className="flex items-center justify-between px-2 py-3 sm:p-4 border-b bg-primary text-primary-foreground min-h-[64px]">
        <h2 className="text-sm sm:text-base md:text-lg font-semibold truncate min-w-0 flex-1 pr-2 text-primary-foreground">
          {product.title}
        </h2>
        <div className="flex items-center gap-1 flex-shrink-0">
          <TooltipProvider>
            {showPanningControls && (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
                      onClick={(e) => {
                        e.stopPropagation();
                        goToPreviousPanningProduct();
                      }}
                      disabled={productPanningQueueLength < 2}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent variant="sidebar">
                    <p>Anterior</p>
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
                      onClick={(e) => {
                        e.stopPropagation();
                        goToNextPanningProduct();
                      }}
                      disabled={productPanningQueueLength < 2}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent variant="sidebar">
                    <p>Siguiente</p>
                  </TooltipContent>
                </Tooltip>
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-primary-foreground/15 text-primary-foreground hidden sm:inline-flex">
                  {productPanningIndex + 1}/{Math.max(1, productPanningQueueLength)}
                </span>
              </>
            )}
            {canEditProduct && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={(e) => { e.stopPropagation(); setEditProductSidebarOpen(true); }}
                    className="p-1 hover:bg-white/10 text-white rounded-lg transition-colors hover:text-white"
                    aria-label="Editar producto"
                  >
                    <Pencil className="w-5 h-5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent variant="sidebar">
                  <p>Editar</p>
                </TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={(e) => { e.stopPropagation(); handleHeaderMinimize(); }}
                  className="p-1 hover:bg-white/10 text-white rounded-lg transition-colors hover:text-white"
                >
                  <ChevronDown className="w-5 h-5" />
                </button>
              </TooltipTrigger>
              <TooltipContent variant="sidebar">
                <p>Minimizar</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={(e) => { e.stopPropagation(); handleClose(); }}
                  className="p-1 hover:bg-white/10 text-white rounded-lg transition-colors hover:text-white"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </TooltipTrigger>
              <TooltipContent variant="sidebar">
                <p>Cerrar</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Content */}
      <ScrollArea ref={scrollAreaRef} className="flex-1 overflow-x-hidden" data-product-sidebar-scroll>
        {!isOnline && (
          <div className="bg-destructive/10 border-b border-destructive/20 p-3 flex items-start gap-3 animate-in slide-in-from-top duration-500">
            <WifiOff className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-semibold text-destructive uppercase tracking-wider">Sin conexión</span>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Tus favoritos y canastos se sincronizarán automáticamente al recuperar internet.
              </p>
            </div>
          </div>
        )}
        <div className="px-2 py-3 sm:p-4 space-y-3 sm:space-y-4" style={{ maxWidth: `${maxWidth}px` }}>
          
          {/* Carousel de imágenes - Primer elemento */}
          {displayImageUrls.length > 0 ? (
            <div className="w-full overflow-hidden relative" style={{ maxWidth: `${maxWidth - 16}px` }}>
              <Carousel className="w-full" style={{ maxWidth: `${maxWidth - 16}px` }}>
                <CarouselContent>
                  {displayImageUrls.map((imageUrl, idx) => (
                    <CarouselItem key={idx} style={{ maxWidth: `${maxWidth - 16}px` }}>
                      <div className="w-full rounded-lg overflow-hidden" style={{ maxWidth: `${maxWidth - 16}px` }}>
                        <div className="relative w-full aspect-square bg-primary/10" style={{ maxWidth: `${maxWidth - 16}px` }}>
                          <img
                            src={imageUrl}
                            alt={`${product.title} - imagen ${idx + 1}`}
                            className="absolute inset-0 w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => handleImageClick(idx)}
                            loading={idx === 0 ? "eager" : "lazy"}
                          />
                        </div>
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                {displayImageUrls.length > 1 && (
                  <>
                    <CarouselPrevious />
                    <CarouselNext />
                  </>
                )}
              </Carousel>
              
              {/* Botón de favoritos en esquina superior derecha */}
              <div className="absolute top-2 right-2 z-20">
                <Button
                  onClick={() => {
                    const wasNotFavorited = !isFavorited(product.id);
                    toggleFavorite({
                      id: product.id,
                      title: product.title,
                      description: product.description,
                      fixed_sale_price: product.buy_now_price ?? product.fixed_sale_price,
                      stock: product.stock,
                      addedAt: Date.now(),
                      store_id: product.store_id,
                      store_name: store?.name || 'Tienda',
                    });
                    // Sonido basado en si SE AGREGÓ o quitó
                    playFavoriteSound(wasNotFavorited);
                    vibrate();
                  }}
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0 rounded-full shadow-md hover:shadow-lg transition-all active:scale-75 duration-75 bg-white/80 dark:bg-gray-900/80 hover:bg-white dark:hover:bg-gray-800 border-0"
                >
                  <Heart 
                    className={`h-4 w-4 transition-all ${isFavorited(product.id) ? 'fill-red-500 text-red-500' : 'text-muted-foreground'}`}
                  />
                </Button>
              </div>
            </div>
          ) : (
            <div className="w-full rounded-lg overflow-hidden" style={{ maxWidth: `${maxWidth - 16}px` }}>
              <div className="relative w-full aspect-square bg-primary/10 flex items-center justify-center">
                <div className="w-full h-full flex items-center justify-center">
                  <ShoppingCart className="h-12 w-12 text-primary/30" />
                </div>
                
                {/* Botón de favoritos en esquina superior derecha del placeholder */}
                <div className="absolute top-2 right-2 z-20">
                  <Button
                    onClick={() => {
                      const wasNotFavorited = !isFavorited(product.id);
                      toggleFavorite({
                        id: product.id,
                        title: product.title,
                        description: product.description,
                        fixed_sale_price: product.buy_now_price ?? product.fixed_sale_price,
                        stock: product.stock,
                        addedAt: Date.now(),
                        store_id: product.store_id,
                        store_name: store?.name || 'Tienda',
                      });
                      playFavoriteSound(wasNotFavorited);
                      vibrate();
                    }}
                    size="sm"
                    variant="ghost"
                    className="h-8 w-8 p-0 rounded-full shadow-md hover:shadow-lg transition-all active:scale-75 duration-75 bg-white/80 dark:bg-gray-900/80 hover:bg-white dark:hover:bg-gray-800 border-0"
                  >
                    <Heart 
                      className={`h-4 w-4 transition-all ${isFavorited(product.id) ? 'fill-red-500 text-red-500' : 'text-muted-foreground'}`}
                    />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Información de Subasta - Destacada inmediatamente después de imágenes */}
          {product.is_auction && (
            <div className="space-y-2 p-2.5 sm:p-3 md:p-4 border-2 border-primary/30 rounded-lg bg-gradient-to-br from-primary/5 to-primary/10 overflow-hidden" style={{ maxWidth: `${maxWidth - 16}px` }}>
              <div className="flex items-center justify-between flex-wrap gap-1">
                <h3 className="text-xs sm:text-sm md:text-base font-bold flex items-center gap-1.5">
                  <Gavel className="h-3.5 w-3.5 sm:h-4 sm:w-4 md:h-5 md:w-5 text-primary flex-shrink-0" /> 
                  <span className="whitespace-nowrap">Subasta</span>
                </h3>
                {product.auction_end_time && isAuctionActive && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3 flex-shrink-0" />
                    <CountdownTimer
                      endTime={new Date(product.auction_end_time)}
                      onAuctionEnded={() => setAuctionEndedByTimer(true)}
                    />
                  </div>
                )}
              </div>
              
              <div className="space-y-2 max-w-full">
                <div className="flex items-baseline justify-between gap-2 flex-wrap">
                  <span className="text-xs sm:text-sm font-medium text-primary whitespace-nowrap">
                    {isPast(new Date(product.auction_end_time)) || auctionEndedByTimer ? 'Precio final:' : 'Oferta actual:'}
                  </span>
                  <FlipCounter value={product.current_bid || product.starting_bid || 0} className="text-lg sm:text-xl md:text-2xl font-semibold" />
                </div>
                
                {product.buy_now_price && isAuctionActive && (
                  <div className="flex items-baseline justify-between gap-2 pt-2 border-t border-primary/20 flex-wrap">
                    <span className="text-xs sm:text-sm font-medium flex items-center gap-1 whitespace-nowrap">
                      <DollarSign className="h-3 w-3 flex-shrink-0" /> 
                      Compra ya:
                    </span>
                    <FlipCounter value={product.buy_now_price} className="text-sm sm:text-base md:text-lg font-semibold" />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Información de Alquiler - Para productos NO subasta */}
          {!product.is_auction && minRentalOption && (
            <div className="flex items-center justify-between p-2.5 sm:p-3 md:p-4 bg-muted/50 rounded-lg border flex-wrap gap-2" style={{ maxWidth: `${maxWidth - 16}px` }}>
              <span className="flex items-center gap-2 text-sm sm:text-base font-semibold text-primary">
                <Tree className="h-4 w-4 sm:h-5 sm:w-5" /> Alquiler
              </span>
              <div className="flex flex-col items-end">
                <span className="text-xs text-muted-foreground">desde</span>
                <FlipCounter value={minRentalOption.price} className="text-lg sm:text-xl font-semibold" />
              </div>
            </div>
          )}

          {/* Tags de condición, envío, ubicación + fallback a categoría/subcategoría */}
          {(detailTagsCount > 0 || categoryString) && (
            <div className="space-y-1" style={{ maxWidth: `${maxWidth - 16}px` }}>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Detalles</p>
              <div className="flex flex-wrap gap-1 sm:gap-1.5">
                {hasRealCondition ? (
                  <div className="flex items-center gap-1 bg-muted px-2 py-1 rounded-md text-xs">
                    <Tag className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                    <span className="truncate max-w-[100px] sm:max-w-none">{translatedCondition}</span>
                  </div>
                ) : null}
                {product.free_shipping ? (
                  <div className="flex items-center gap-1 bg-green-100 text-green-800 px-2 py-1 rounded-md text-xs font-medium whitespace-nowrap">
                    <Truck className="h-3 w-3 flex-shrink-0" />
                    <span>Envío gratis</span>
                  </div>
                ) : null}
                {locationString ? (
                  <div className="flex items-center gap-1 bg-muted px-2 py-1 rounded-md text-xs">
                    <MapPin className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                    <span className="truncate max-w-[80px] sm:max-w-[120px]">{locationString}</span>
                  </div>
                ) : null}
                {/* Fallback: Mostrar categoría y subcategoría si no hay otros tags */}
                {detailTagsCount === 0 && categoryString && (
                  <>
                    <div className="flex items-center gap-1 bg-muted px-2 py-1 rounded-md text-xs">
                      <Tag className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                      <span className="truncate">{toSentenceCase(translateProductCategoryLabel(product.product_type))}</span>
                    </div>
                    {product.product_type === 'groceries' && product.details?.groceries_subcategory && (
                      <div className="flex items-center gap-1 bg-muted px-2 py-1 rounded-md text-xs">
                        <Tag className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                        <span className="truncate">{toSentenceCase(translateGroceriesSubcategoryLabel(product.details.groceries_subcategory))}</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Detalles del producto - Solo los más relevantes */}
          {hasTechnicalDetails && (
            <div className="flex flex-wrap gap-1 sm:gap-1.5 overflow-hidden" style={{ maxWidth: `${maxWidth - 16}px` }}>
              {product.product_type === 'vehicle' && product.details.vehicle && (
                <>
                  {product.details.vehicle.year && (
                    <span className="flex items-center bg-muted text-muted-foreground px-2 py-1 rounded-full text-xs">
                      <Car className="h-3 w-3 mr-1" />
                      {product.details.vehicle.year}
                    </span>
                  )}
                  {product.details.vehicle.kilometers && (
                    <span className="flex items-center bg-muted text-muted-foreground px-2 py-1 rounded-full text-xs">
                      <Car className="h-3 w-3 mr-1" />
                      {new Intl.NumberFormat('es-AR').format(product.details.vehicle.kilometers)} km
                    </span>
                  )}
                  {product.details.vehicle.fuel_type && (
                    <span className="flex items-center bg-muted text-muted-foreground px-2 py-1 rounded-full text-xs">
                      <Beaker className="h-3 w-3 mr-1" />
                      {translateVehicleDetail('fuel_type', product.details.vehicle.fuel_type)}
                    </span>
                  )}
                  {product.details.vehicle.transmission && (
                    <span className="flex items-center bg-muted text-muted-foreground px-2 py-1 rounded-full text-xs">
                      <Repeat className="h-3 w-3 mr-1" />
                      {translateVehicleDetail('transmission', product.details.vehicle.transmission)}
                    </span>
                  )}
                  {product.details.vehicle.color && (
                    <span className="flex items-center bg-muted text-muted-foreground px-2 py-1 rounded-full text-xs">
                      <Paintbrush className="h-3 w-3 mr-1" />
                      {product.details.vehicle.color}
                    </span>
                  )}
                  {product.details.vehicle.gnc && (
                    <span className="flex items-center bg-warning-muted text-warning px-2 py-1 rounded-full text-xs font-semibold">
                      GNC
                    </span>
                  )}
                </>
              )}
              {product.product_type === 'electronics' && product.details.electronics && (
                <>
                  {product.details.electronics.brand && (
                    <span className="flex items-center bg-muted text-muted-foreground px-2 py-1 rounded-full text-xs">
                      <Info className="h-3 w-3 mr-1" />
                      {product.details.electronics.brand}
                    </span>
                  )}
                  {product.details.electronics.model && (
                    <span className="flex items-center bg-muted text-muted-foreground px-2 py-1 rounded-full text-xs">
                      <Tag className="h-3 w-3 mr-1" />
                      {product.details.electronics.model}
                    </span>
                  )}
                  {product.details.electronics.storage && (
                    <span className="flex items-center bg-muted text-muted-foreground px-2 py-1 rounded-full text-xs">
                      <HardDrive className="h-3 w-3 mr-1" />
                      {product.details.electronics.storage}
                    </span>
                  )}
                  {product.details.electronics.ram && (
                    <span className="flex items-center bg-muted text-muted-foreground px-2 py-1 rounded-full text-xs">
                      <Info className="h-3 w-3 mr-1" />
                      {product.details.electronics.ram}
                    </span>
                  )}
                </>
              )}
            </div>
          )}

          {/* Información del publicador */}
          {shouldShowPublisherBlock && (
            <div className="space-y-1" style={{ maxWidth: `${maxWidth - 16}px` }}>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Ofrecido por</p>
              <div className="p-2 sm:p-2.5 md:p-3 bg-muted/50 rounded-lg overflow-hidden">
              <div className="flex items-center gap-2 sm:gap-2.5 max-w-full min-w-0">
                {hasStorePublisher ? (
                  <>
                    <Link
                      to={`/${effectiveStore.slug || effectiveStore.id}`}
                      onClick={handleMinimize}
                      className="flex-shrink-0"
                    >
                      <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-md overflow-hidden bg-muted flex items-center justify-center">
                        {effectiveStore.avatar_url ? (
                          <img className="h-full w-full object-cover" src={effectiveStore.avatar_url} alt={storeDisplayName || 'Tienda'} />
                        ) : (
                          <span className="text-muted-foreground text-sm font-medium">
                            {String(storeDisplayName).charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                    </Link>
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <Link
                        to={`/${effectiveStore.slug || effectiveStore.id}`}
                        onClick={handleMinimize}
                        className="font-medium text-xs sm:text-sm hover:underline block truncate"
                      >
                        {storeDisplayName}
                      </Link>
                      {publishedString ? (
                        <div className="text-xs text-muted-foreground truncate">
                          {publishedString}
                        </div>
                      ) : null}
                    </div>
                  </>
                ) : isLoadingSellerData ? (
                  <div className="flex items-center gap-3 animate-pulse w-full">
                    <div className="h-10 w-10 rounded-full bg-muted flex-shrink-0" />
                    <div className="flex-1 space-y-1">
                      <div className="h-3 w-24 bg-muted rounded" />
                      <div className="h-2 w-16 bg-muted rounded" />
                    </div>
                  </div>
                ) : hasProfilePublisher ? (
                  <>
                    <Link to={`/${sellerUsername}`} className="flex-shrink-0">
                      <Avatar className="h-9 w-9 sm:h-10 sm:w-10">
                        <AvatarImage src={sellerAvatarUrl || undefined} alt={sellerUsername || 'Vendedor'} />
                        <AvatarFallback>
                          {String(sellerUsername).charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                    </Link>
                    <div className="flex-1 min-w-0">
                      <Link to={`/${sellerUsername}`} className="font-medium text-xs sm:text-sm hover:underline block truncate">
                        {sellerUsername}
                      </Link>
                      {publishedString ? <div className="text-xs text-muted-foreground">{publishedString}</div> : null}
                    </div>
                  </>
                ) : null}
              </div>
            </div>
            </div>
          )}

          {/* Autor */}
          {hasAuthor && (
            <div className="space-y-1" style={{ maxWidth: `${maxWidth - 16}px` }}>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Autor</p>
              <p className="text-sm text-foreground">{product.author}</p>
            </div>
          )}

          {/* Descripción */}
          {hasDescription && (
            <div className="space-y-1 overflow-hidden" style={{ maxWidth: `${maxWidth - 16}px` }}>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Descripción</p>
              <div
                className="prose prose-sm max-w-full text-xs sm:text-sm leading-relaxed text-muted-foreground [&>p]:mb-2 [&>ul]:mb-2 [&>ol]:mb-2 break-words overflow-wrap-anywhere"
                dangerouslySetInnerHTML={{ __html: product.description }}
              />
            </div>
          )}

          {/* Rating promedio */}
          {averageRating !== null && (
            <div className="p-2 sm:p-2.5 md:p-3 bg-secondary/20 rounded-lg border border-primary/20 flex items-center justify-between" style={{ maxWidth: `${maxWidth - 16}px` }}>
              <div className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map((starIndex) => {
                  const isFilled = averageRating >= starIndex;
                  const isHalf = averageRating >= starIndex - 0.5 && averageRating < starIndex;

                  return (
                    <div key={starIndex} className="relative h-4 w-4">
                      <Star className="h-4 w-4 text-muted-foreground absolute inset-0" />
                      {(isFilled || isHalf) && (
                        <div
                          className="absolute inset-0 overflow-hidden"
                          style={{ width: isFilled ? '100%' : '50%' }}
                        >
                          <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="text-sm">
                <span className="font-semibold">{averageRating.toFixed(1)}</span>
                <span className="text-muted-foreground text-xs"> / 5</span>
              </div>
            </div>
          )}

          {/* Comentarios de catálogo */}
          {product.product_catalog_id && (
            <ProductCatalogComments 
              productCatalogId={product.product_catalog_id}
              session={session}
            />
          )}

          {/* Activity Feed o Chat Form */}
          {product.id && (
            <div className="pt-3 border-t">
              {product.is_auction ? (
                <ProductActivityFeed
                  productId={product.id}
                  productCatalogId={product.product_catalog_id}
                  session={session}
                  sellerId={product.seller_id}
                />
              ) : (
                <ProductChatForm
                  productId={product.id}
                  session={session}
                  storeId={effectiveStore?.id}
                  storeName={effectiveStore?.name}
                  lastPurchaseInfo={lastPurchaseInfo}
                  isAuction={product.is_auction}
                  onChatAdded={() => {
                    getProductChatsByProductId(product.id).then(chats => {
                      setProductChats(chats || []);
                    });
                    getAverageRatingByProductId(product.id).then(rating => {
                      setAverageRating(rating);
                    });
                  }}
                />
              )}
            </div>
          )}

        </div>
      </ScrollArea>

      {/* Footer - Compacto y funcional con padding optimizado para mobile */}
      <div className="px-4 py-4 sm:px-5 sm:py-5 border-t space-y-3 bg-background overflow-hidden relative pb-[calc(1.5rem+env(safe-area-inset-bottom,0px))] sm:pb-5" style={{ maxWidth: `${maxWidth}px` }}>
        
        {/* Para subastas activas: Formulario de puja */}
        {product.is_auction && isAuctionActive && (
          <MiniBidForm
            productId={product.id}
            session={session}
            currentBid={product.current_bid}
            startingBid={product.starting_bid}
            buyNowPrice={product.buy_now_price}
            sellerId={product.seller_id}
            productTitle={product.title}
            isAuctionActive={isAuctionActive}
          />
        )}

        {/* Para productos NO subasta: Botón de agregar al canasto con transformación a selector de cantidad */}
        {!product.is_auction && (product.buy_now_price != null || product.fixed_sale_price != null || product.fixed_rental_price != null) && product.store_id && (
          <div className="flex items-center justify-between gap-2 sm:gap-4">
            <div className="flex-shrink-0">
              <span className="text-[10px] sm:text-xs text-muted-foreground block mb-0.5">Precio</span>
              <span className="text-base sm:text-xl font-bold text-primary whitespace-nowrap">
                {formatCurrency(product.buy_now_price ?? product.fixed_sale_price ?? 0, 'ARS')}
              </span>
            </div>

            {/* Elemento central: Chip de canasto */}
            <div className="flex-1 flex justify-center overflow-hidden">
              {isAddingToCart && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-[10px] font-medium bg-primary/10 text-primary border border-primary/20 animate-pulse whitespace-nowrap">
                  <Loader2 className="w-2.5 h-2.5 mr-1 animate-spin" />
                  Agregando...
                </span>
              )}
              {cartItemId && !isAddingToCart && currentQuantity > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    handleClose();
                    setTimeout(() => {
                      openCheckoutSidebar({
                        cartOnly: true,
                        preferredStoreId: product.store_id,
                      });
                    }, 320);
                  }}
                  className="inline-flex items-center px-2 py-1 rounded-full text-[10px] font-medium bg-primary/10 text-primary border border-primary/20 animate-in fade-in zoom-in duration-300 whitespace-nowrap hover:bg-primary/15 transition-colors"
                  title="Ir al canasto"
                >
                  <ShoppingCart className="w-2.5 h-2.5 mr-1" />
                  En canasto
                </button>
              )}
            </div>

            <div className="relative h-[43px] flex items-center justify-end flex-shrink-0 gap-2">
              {cartItemId && currentQuantity > 0 ? (
                // Mostrar QuantitySelector con la misma estética que en las cards
                <QuantitySelector
                  key={`qty-${cartItemId}-${product.id}`}
                  cartItemId={cartItemId}
                  quantity={currentQuantity}
                  onUpdateQuantity={(id, quantityCalculator) => {
                    const nextQty = quantityCalculator(currentQuantity);
                    if (nextQty === currentQuantity) return;

                    // Actualización optimista inmediata
                    setCurrentQuantity(nextQty);
                    setUpdatingProductIds(prev => new Set(prev).add(product.id));
                    window.dispatchEvent(new CustomEvent('cart-updated'));

                    (async () => {
                      try {
                        if (session?.user?.id) {
                          await updateCartItem(id, nextQty);
                        } else {
                          updateTempCartItemQuantity(product.id, nextQty);
                        }
                        if (nextQty === 0 && !productPanningActive && productPanningQueueLength > 0) {
                          toggleProductPanningPlayback();
                        }
                        // Disparar evento de nuevo tras confirmación para asegurar sync global
                        window.dispatchEvent(new CustomEvent('cart-updated'));
                      } catch (e) {
                        setCurrentQuantity(currentQuantity); // Revertir en caso de error
                        toast({
                          title: '❌ Error',
                          description: (e as any)?.message || 'No pudimos actualizar el canasto',
                          variant: 'destructive',
                        });
                      } finally {
                        setUpdatingProductIds(prev => {
                          const next = new Set(prev);
                          next.delete(product.id);
                          return next;
                        });
                      }
                    })();
                  }}
                  onPlayBeep={playBeep}
                  onVibrate={vibrate}
                  disabled={updatingProductIds.has(product.id)}
                />
              ) : (
                // Botón circular de agregar (estética idéntica a la card)
                <Button
                  onClick={async () => {
                    vibrate('add');
                    playBeep('add');
                    pausePanningForCart();
                    
                    // Transformación visual inmediata (Hacia QuantitySelector con cantidad 1)
                    setCartItemId(`pending-${product.id}`); // ID temporal para activar el selector
                    setCurrentQuantity(1);
                    setIsAddingToCart(true); // Ocultar chip hasta que termine
                    setUpdatingProductIds(prev => new Set(prev).add(product.id));
                    
                    // Actualización optimista del contador global
                    window.dispatchEvent(new CustomEvent('cart-updated'));

                    try {
                      if (session?.user?.id) {
                        // Agregar al canasto permanente si hay sesión
                        const cartItem = await addCartItem(session.user.id, product.id, 1);
                        window.dispatchEvent(new CustomEvent('cart-updated'));
                        
                        // Obtener el cartItemId final
                        setCartItemId(cartItem.id);
                      } else {
                        // Agregar a canasto temporal si no hay sesión
                        addToTempCart(product, 1, product.store_id);
                        window.dispatchEvent(new CustomEvent('cart-updated'));
                        
                        setCartItemId(product.id);
                      }
                      // Transacción exitosa, el chip ahora puede mostrarse
                      setIsAddingToCart(false);
                    } catch (e) {
                      console.error(e);
                      // Revertir ante error
                      setCartItemId(null);
                      setCurrentQuantity(0);
                      setIsAddingToCart(false);
                      toast({
                        title: 'Error',
                        description: (e as any)?.message || 'No pudimos agregar el producto. Probá de nuevo.',
                        variant: 'destructive',
                      });
                    } finally {
                      setUpdatingProductIds(prev => {
                        const next = new Set(prev);
                        next.delete(product.id);
                        return next;
                      });
                    }
                  }}
                  disabled={updatingProductIds.has(product.id)}
                  className="h-[43px] px-4 rounded-full shadow-lg hover:shadow-xl transition-all active:scale-95 duration-75 flex items-center justify-center gap-2 shrink-0"
                >
                  <Plus className="h-5 w-5 shrink-0" />
                  <span className="text-sm font-semibold whitespace-nowrap">Agregar al canasto</span>
                </Button>
              )}
              {showPanningControls && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className={cn(
                          'relative h-[43px] w-[43px] rounded-full text-primary transition-all duration-300 ease-out border border-primary/15 hover:text-primary overflow-visible',
                          productPanningActive
                            ? 'bg-primary/12 shadow-[0_0_0_1px_rgba(99,102,241,0.08),0_0_18px_rgba(99,102,241,0.14)] hover:bg-primary/16 scale-100'
                            : 'bg-muted/70 hover:bg-muted scale-95'
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (productPanningActive) {
                            pauseProductPanning();
                          } else {
                            resumeProductPanning({ advanceToNext: true });
                          }
                        }}
                      >
                        <svg
                          className="absolute inset-0 -rotate-90 pointer-events-none"
                          viewBox="0 0 43 43"
                          aria-hidden="true"
                        >
                          <circle
                            cx="21.5"
                            cy="21.5"
                            r={panningRingRadius}
                            fill="none"
                            stroke="currentColor"
                            strokeOpacity="0.16"
                            strokeWidth="2"
                          />
                          <circle
                            cx="21.5"
                            cy="21.5"
                            r={panningRingRadius}
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeDasharray={panningRingCircumference}
                            strokeDashoffset={panningRingCircumference * (1 - panningProgressRatio)}
                          />
                        </svg>
                        {productPanningActive ? (
                          <Pause className="w-4 h-4 transition-transform duration-300 ease-out scale-100" />
                        ) : (
                          <Play className="w-4 h-4 transition-transform duration-300 ease-out translate-x-[1px] scale-100" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent variant="sidebar">
                      <p>{productPanningActive ? 'Pausar' : 'Reanudar'}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>
        )}
      </div>
      </>
    )}
  </div>
  <ImageViewer
    imageUrls={displayImageUrls}
    startIndex={viewerStartIndex}
    isOpen={isImageViewerOpen}
    onClose={handleCloseImageViewer}
  />
  {product?.id && (
    <EditProductSidebar
      isOpen={editProductSidebarOpen}
      onClose={handleEditProductClose}
      productId={product.id}
      onUpdated={handleEditProductUpdated}
    />
  )}
</>
);
};
